import { useCallback, createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Encrypt } from '../utils/encryption';
import { userManagementApi } from '../config/api';
import { Session } from '../storage/session';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setTokenState] = useState(() => Session.get()?.token ?? null);
  const refreshingRef = useRef(false);
  const navigate = useNavigate();

  const setToken = (initialToken) => {
    if (initialToken == null) {
      setTokenState(null);
      return;
    }
    const decoded = decodeToken(initialToken);
    if (!decoded?.exp || decoded.exp < Date.now() / 1000) {
      console.warn('Tried to set expired token');
      setTokenState(null);
    } else {
      setTokenState(initialToken);
    }
  };

  const decodeToken = (token) => {
    try {
      return jwtDecode(token);
    } catch {
      return null;
    }
  };

  const logout = useCallback(() => {
    setTokenState(null);
    Session.clear();
    navigate('/login', { replace: true });
  }, [navigate]);

  useEffect(() => {
    let intervalId;

    if (token) {
      const isExpiringSoon = (jwt, withinSeconds = 606) => {
        const decoded = decodeToken(jwt);
        if (!decoded?.exp) return true;

        const currentTime = Date.now() / 1000;
        return decoded.exp - currentTime < withinSeconds;
      };

      const refreshToken = async () => {
        if (refreshingRef.current) return;
        refreshingRef.current = true;

        try {
          const response = await axios.post(
            userManagementApi,
            { ...Encrypt(token), operationType: 'extendtoken' },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );

          const newToken = response.data?.token;
          if (newToken) {
            setTokenState(newToken);
            const prev = Session.get();
            Session.save({
              ...prev,
              token: newToken,
              status: prev?.status || 'success',
            });
          } else {
            console.warn('No valid token from extend');
            logout();
          }
        } catch (error) {
          console.error('Failed to refresh token:', error);
          logout();
        } finally {
          refreshingRef.current = false;
        }
      };

      const checkAndRefresh = () => {
        if (isExpiringSoon(token)) {
          refreshToken();
        }
      };

      checkAndRefresh();
      intervalId = setInterval(checkAndRefresh, 5 * 60 * 1000);
    }

    return () => clearInterval(intervalId);
  }, [token, logout]);

  return (
    <AuthContext.Provider value={{ token, setToken, logout, decodeToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
